import React, { useState, useCallback, useMemo } from 'react';
import { 
    Search, 
    RefreshCw, 
    ChevronDown, 
    ChevronRight, 
    Copy, 
    Check, 
    X, 
    CheckCircle,
    XCircle,
    CreditCard,
    MapPin,
    Receipt,
    FileText
} from 'lucide-react';
import { 
    useReceiptRequests, 
    updateReceiptRequestStatus, 
    updateBulkReceiptRequestStatus,
    ReceiptRequest,
    ReceiptRequestFilters 
} from '../../lib/api';
import Pagination from '../../components/common/Pagination';
import LoadingState from '../../components/common/LoadingState';
import ErrorState from '../../components/common/ErrorState';
import { 
    formatDanishNumber, 
    getReceiptStatusColor, 
    formatLicensePlate,
    formatDanishDate,
    generateReceiptEmailTemplate
} from '../../lib/utils';

// Debounce hook for search
function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

const ReceiptRequestsPage: React.FC = () => {
    // Filter state
    const [filters, setFilters] = useState<ReceiptRequestFilters>({
        status: 'pending',
        page: 1,
        limit: 50,
    });
    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebounce(searchInput, 300);

    // UI state
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
    const [bulkProcessing, setBulkProcessing] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    // Apply debounced search to filters
    const activeFilters = useMemo(() => ({
        ...filters,
        search: debouncedSearch || undefined,
    }), [filters, debouncedSearch]);

    const { requests, pagination, isLoading, error, mutate } = useReceiptRequests(activeFilters);

    // Show toast notification
    const showToast = useCallback((message: string, type: 'success' | 'error') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    }, []);

    // Copy to clipboard
    const copyToClipboard = useCallback(async (text: string, fieldId: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldId);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            showToast('Failed to copy to clipboard', 'error');
        }
    }, [showToast]);

    // Toggle row expansion
    const toggleRowExpansion = useCallback((requestId: string) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(requestId)) {
                next.delete(requestId);
            } else {
                next.add(requestId);
            }
            return next;
        });
    }, []);

    // Toggle row selection
    const toggleRowSelection = useCallback((requestId: string) => {
        setSelectedRows(prev => {
            const next = new Set(prev);
            if (next.has(requestId)) {
                next.delete(requestId);
            } else {
                next.add(requestId);
            }
            return next;
        });
    }, []);

    // Select/deselect all pending requests
    const toggleSelectAll = useCallback(() => {
        const pendingRequests = requests.filter(r => r.status === 'pending');
        const allSelected = pendingRequests.every(r => selectedRows.has(r.requestId));
        
        if (allSelected) {
            setSelectedRows(new Set());
        } else {
            setSelectedRows(new Set(pendingRequests.map(r => r.requestId)));
        }
    }, [requests, selectedRows]);

    // Update single request status
    const handleUpdateStatus = useCallback(async (requestId: string, status: 'sent' | 'cancelled') => {
        setProcessingIds(prev => new Set(prev).add(requestId));
        try {
            await updateReceiptRequestStatus(requestId, status);
            showToast(`Request marked as ${status}`, 'success');
            mutate();
            setSelectedRows(prev => {
                const next = new Set(prev);
                next.delete(requestId);
                return next;
            });
        } catch (err: any) {
            showToast(err.message || 'Failed to update status', 'error');
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.delete(requestId);
                return next;
            });
        }
    }, [mutate, showToast]);

    // Bulk update status
    const handleBulkUpdate = useCallback(async (status: 'sent' | 'cancelled') => {
        if (selectedRows.size === 0) return;
        
        setBulkProcessing(true);
        try {
            const result = await updateBulkReceiptRequestStatus(Array.from(selectedRows), status);
            showToast(`${result.updated} requests marked as ${status}`, 'success');
            mutate();
            setSelectedRows(new Set());
        } catch (err: any) {
            showToast(err.message || 'Failed to update requests', 'error');
        } finally {
            setBulkProcessing(false);
        }
    }, [selectedRows, mutate, showToast]);

    // Handle filter changes
    const handleFilterChange = useCallback((key: keyof ReceiptRequestFilters, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: key !== 'page' ? 1 : value, // Reset to page 1 when changing filters
        }));
    }, []);

    // Handle page change
    const handlePageChange = useCallback((page: number) => {
        handleFilterChange('page', page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [handleFilterChange]);

    // Copy email template
    const handleCopyEmailTemplate = useCallback((request: ReceiptRequest) => {
        const template = generateReceiptEmailTemplate(request);
        copyToClipboard(template, `template-${request.requestId}`);
        showToast('Email template copied!', 'success');
    }, [copyToClipboard, showToast]);

    // Count pending requests
    const pendingCount = useMemo(() => {
        return requests.filter(r => r.status === 'pending').length;
    }, [requests]);

    // Check if all pending are selected
    const allPendingSelected = useMemo(() => {
        const pendingRequests = requests.filter(r => r.status === 'pending');
        return pendingRequests.length > 0 && pendingRequests.every(r => selectedRows.has(r.requestId));
    }, [requests, selectedRows]);

    if (isLoading && !requests.length) {
        return <LoadingState message="Loading receipt requests..." />;
    }

    if (error) {
        return <ErrorState error={error.message || 'An error occurred'} onRetry={() => mutate()} />;
    }

    const statusOptions = [
        { value: '', label: 'All Statuses' },
        { value: 'pending', label: 'Pending' },
        { value: 'sent', label: 'Sent' },
        { value: 'cancelled', label: 'Cancelled' },
    ];

    return (
        <div className="space-y-6">
            {/* Toast Notification */}
            {toast && (
                <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
                    toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                }`}>
                    {toast.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                    {toast.message}
                </div>
            )}

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-bold text-gray-900">Receipt Requests</h1>
                        {pagination && pagination.total > 0 && (
                            <span className="px-2.5 py-0.5 text-sm font-medium bg-yellow-100 text-yellow-800 rounded-full">
                                {pendingCount} pending
                            </span>
                        )}
                    </div>
                    <p className="text-gray-600 mt-1">Manage guest receipt requests</p>
                </div>
                <button
                    onClick={() => mutate()}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                    title="Refresh data"
                >
                    <RefreshCw className="h-5 w-5" />
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white border rounded-lg p-4">
                <div className="flex flex-col md:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Search
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by email or receipt number..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                    </div>

                    {/* Status Filter */}
                    <div className="w-full md:w-48">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Status
                        </label>
                        <select
                            value={filters.status || ''}
                            onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Results per page */}
                    <div className="w-full md:w-32">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Per page
                        </label>
                        <select
                            value={filters.limit || 50}
                            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Bulk Actions Bar */}
            {selectedRows.size > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-center justify-between">
                    <span className="text-sm font-medium text-indigo-800">
                        {selectedRows.size} request{selectedRows.size > 1 ? 's' : ''} selected
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handleBulkUpdate('sent')}
                            disabled={bulkProcessing}
                            className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            <Check className="h-4 w-4" />
                            Mark All as Sent
                        </button>
                        <button
                            onClick={() => handleBulkUpdate('cancelled')}
                            disabled={bulkProcessing}
                            className="px-4 py-2 bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            <X className="h-4 w-4" />
                            Cancel All
                        </button>
                        <button
                            onClick={() => setSelectedRows(new Set())}
                            className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
                        >
                            Clear Selection
                        </button>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="bg-white border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left">
                                    <input
                                        type="checkbox"
                                        checked={allPendingSelected}
                                        onChange={toggleSelectAll}
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Receipt #
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Email
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Parking Lot
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date/Time
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Duration
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Amount
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    License Plate
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Card
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {requests.length === 0 ? (
                                <tr>
                                    <td colSpan={11} className="px-6 py-12 text-center text-gray-500">
                                        <Receipt className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                                        <p className="text-lg font-medium">No receipt requests found</p>
                                        <p className="text-sm mt-1">
                                            {filters.status === 'pending' 
                                                ? 'All caught up! No pending requests.' 
                                                : 'Try adjusting your filters.'}
                                        </p>
                                    </td>
                                </tr>
                            ) : (
                                requests.map((request) => (
                                    <React.Fragment key={request.requestId}>
                                        {/* Main Row */}
                                        <tr 
                                            className={`hover:bg-gray-50 cursor-pointer ${
                                                expandedRows.has(request.requestId) ? 'bg-gray-50' : ''
                                            }`}
                                            onClick={() => toggleRowExpansion(request.requestId)}
                                        >
                                            <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                                                {request.status === 'pending' && (
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedRows.has(request.requestId)}
                                                        onChange={() => toggleRowSelection(request.requestId)}
                                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                    />
                                                )}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    {expandedRows.has(request.requestId) ? (
                                                        <ChevronDown className="h-4 w-4 text-gray-400" />
                                                    ) : (
                                                        <ChevronRight className="h-4 w-4 text-gray-400" />
                                                    )}
                                                    <span className="font-mono font-semibold text-sm text-gray-900">
                                                        {request.sessionSnapshot.receiptNumber}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm text-gray-900">{request.email}</span>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            copyToClipboard(request.email, `email-${request.requestId}`);
                                                        }}
                                                        className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                                        title="Copy email"
                                                    >
                                                        {copiedField === `email-${request.requestId}` ? (
                                                            <Check className="h-3.5 w-3.5 text-green-600" />
                                                        ) : (
                                                            <Copy className="h-3.5 w-3.5" />
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {request.sessionSnapshot.lotName || request.sessionSnapshot.lotId}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {formatDanishDate(request.sessionSnapshot.startTime)}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {request.sessionSnapshot.durationMinutes} min
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {formatDanishNumber(request.sessionSnapshot.pricing.total)} {request.sessionSnapshot.pricing.currency}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                                                {formatLicensePlate(request.sessionSnapshot.licensePlate)}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {request.sessionSnapshot.maskedPan 
                                                    ? `••••${request.sessionSnapshot.maskedPan.slice(-4)}`
                                                    : '-'}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getReceiptStatusColor(request.status)}`}>
                                                    {request.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                {request.status === 'pending' ? (
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleUpdateStatus(request.requestId, 'sent')}
                                                            disabled={processingIds.has(request.requestId)}
                                                            className="px-3 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                                                        >
                                                            <Check className="h-3 w-3" />
                                                            Sent
                                                        </button>
                                                        <button
                                                            onClick={() => handleUpdateStatus(request.requestId, 'cancelled')}
                                                            disabled={processingIds.has(request.requestId)}
                                                            className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                                                        >
                                                            <X className="h-3 w-3" />
                                                            Cancel
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="text-xs text-gray-500">
                                                        {request.processedAt && (
                                                            <div>{formatDanishDate(request.processedAt)}</div>
                                                        )}
                                                        {request.processedBy && (
                                                            <div className="text-gray-400">by {request.processedBy}</div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>

                                        {/* Expanded Detail Row */}
                                        {expandedRows.has(request.requestId) && (
                                            <tr className="bg-gray-50">
                                                <td colSpan={11} className="px-6 py-6">
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                        {/* Receipt Info */}
                                                        <div className="space-y-4">
                                                            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                                                <Receipt className="h-4 w-4" />
                                                                Receipt Details
                                                            </h4>
                                                            <div className="space-y-2 text-sm">
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-500">Receipt Number:</span>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-mono font-medium">{request.sessionSnapshot.receiptNumber}</span>
                                                                        <button
                                                                            onClick={() => copyToClipboard(request.sessionSnapshot.receiptNumber, `receipt-${request.requestId}`)}
                                                                            className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                                                        >
                                                                            {copiedField === `receipt-${request.requestId}` ? (
                                                                                <Check className="h-3 w-3 text-green-600" />
                                                                            ) : (
                                                                                <Copy className="h-3 w-3" />
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-500">Email:</span>
                                                                    <div className="flex items-center gap-2">
                                                                        <span>{request.email}</span>
                                                                        <button
                                                                            onClick={() => copyToClipboard(request.email, `email-detail-${request.requestId}`)}
                                                                            className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                                                        >
                                                                            {copiedField === `email-detail-${request.requestId}` ? (
                                                                                <Check className="h-3 w-3 text-green-600" />
                                                                            ) : (
                                                                                <Copy className="h-3 w-3" />
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-500">Requested:</span>
                                                                    <span>{formatDanishDate(request.createdAt)}</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-500">Status:</span>
                                                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${getReceiptStatusColor(request.status)}`}>
                                                                        {request.status}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Parking Info */}
                                                        <div className="space-y-4">
                                                            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                                                <MapPin className="h-4 w-4" />
                                                                Parking Details
                                                            </h4>
                                                            <div className="space-y-2 text-sm">
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-500">Location:</span>
                                                                    <span>{request.sessionSnapshot.lotName || request.sessionSnapshot.lotId}</span>
                                                                </div>
                                                                {request.sessionSnapshot.areaNo && (
                                                                    <div className="flex justify-between">
                                                                        <span className="text-gray-500">Area Number:</span>
                                                                        <span>{request.sessionSnapshot.areaNo}</span>
                                                                    </div>
                                                                )}
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-500">License Plate:</span>
                                                                    <span className="font-mono">{formatLicensePlate(request.sessionSnapshot.licensePlate)}</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-500">Start:</span>
                                                                    <span>{formatDanishDate(request.sessionSnapshot.startTime)}</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-500">End:</span>
                                                                    <span>{formatDanishDate(request.sessionSnapshot.endTime)}</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-500">Duration:</span>
                                                                    <span>{request.sessionSnapshot.durationMinutes} minutes</span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Payment Info */}
                                                        <div className="space-y-4">
                                                            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                                                <CreditCard className="h-4 w-4" />
                                                                Payment Details
                                                            </h4>
                                                            <div className="space-y-2 text-sm">
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-500">Parking Cost:</span>
                                                                    <span>{formatDanishNumber(request.sessionSnapshot.pricing.parkingCost)} {request.sessionSnapshot.pricing.currency}</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-500">Service Fee:</span>
                                                                    <span>{formatDanishNumber(request.sessionSnapshot.pricing.serviceFee)} {request.sessionSnapshot.pricing.currency}</span>
                                                                </div>
                                                                <div className="flex justify-between font-semibold border-t pt-2 mt-2">
                                                                    <span className="text-gray-700">Total:</span>
                                                                    <span>{formatDanishNumber(request.sessionSnapshot.pricing.total)} {request.sessionSnapshot.pricing.currency}</span>
                                                                </div>
                                                                <div className="flex justify-between pt-2">
                                                                    <span className="text-gray-500">Card:</span>
                                                                    <span>
                                                                        {request.sessionSnapshot.cardType || 'Card'} 
                                                                        {request.sessionSnapshot.maskedPan && ` ••••${request.sessionSnapshot.maskedPan.slice(-4)}`}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="mt-6 pt-4 border-t flex flex-wrap gap-3">
                                                        <button
                                                            onClick={() => handleCopyEmailTemplate(request)}
                                                            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
                                                        >
                                                            <FileText className="h-4 w-4" />
                                                            {copiedField === `template-${request.requestId}` ? 'Copied!' : 'Copy Email Template'}
                                                        </button>
                                                        {request.status === 'pending' && (
                                                            <>
                                                                <button
                                                                    onClick={() => handleUpdateStatus(request.requestId, 'sent')}
                                                                    disabled={processingIds.has(request.requestId)}
                                                                    className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                                                >
                                                                    <Check className="h-4 w-4" />
                                                                    Mark as Sent
                                                                </button>
                                                                <button
                                                                    onClick={() => handleUpdateStatus(request.requestId, 'cancelled')}
                                                                    disabled={processingIds.has(request.requestId)}
                                                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                    Cancel Request
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        Showing {((pagination.page - 1) * (filters.limit || 50)) + 1} to{' '}
                        {Math.min(pagination.page * (filters.limit || 50), pagination.total)} of{' '}
                        {pagination.total.toLocaleString()} results
                    </div>
                    <Pagination 
                        pagination={{
                            page: pagination.page,
                            pages: pagination.pages,
                            total: pagination.total,
                            hasNext: pagination.page < pagination.pages,
                            hasPrev: pagination.page > 1,
                        }} 
                        onPageChange={handlePageChange} 
                    />
                </div>
            )}
        </div>
    );
};

export default ReceiptRequestsPage;

